import React from 'react';
import { MapNode } from './useMapLayout';
import { CapabilityEntry, CapabilityDomain } from '../../../types/persona_engine';
import { getDomainColor } from '../../../utils/colorUtils';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
}

const DOMAIN_BG_DARK: Partial<Record<CapabilityDomain, string>> = {
  cognitive:       '#1e3a5f',
  communicative:   '#4a1d96',
  technical:       '#1e1b4b',
  creative:        '#701a75',
  interpersonal:   '#14532d',
  domain_specific: '#374151',
  ethical:         '#14532d',
  strategic:       '#7c2d12',
  analytical:      '#1e3a5f',
  meta_cognitive:  '#312e81',
};

const DOMAIN_BG_LIGHT: Partial<Record<CapabilityDomain, string>> = {
  cognitive:       '#dbeafe',
  communicative:   '#ede9fe',
  technical:       '#e0e7ff',
  creative:        '#fce7f3',
  interpersonal:   '#dcfce7',
  domain_specific: '#f3f4f6',
  ethical:         '#dcfce7',
  strategic:       '#ffedd5',
  analytical:      '#dbeafe',
  meta_cognitive:  '#e0e7ff',
};

/**
 * Full HTML overlay for capability_cluster at high zoom (> LOD_NODES).
 * Renders a CSS grid of capability cards — each showing:
 *   - top accent bar (score-tier color)
 *   - score ring + label + tier badge
 *   - domain badge
 *   - report preview (first line, truncated)
 *   - floor / ceiling pills
 * pointer-events: none — display only.
 */
const CapabilityClusterOverlay: React.FC<Props> = ({
  node,
  transform,
  canvasWidth,
  canvasHeight,
  darkMode,
}) => {
  if (!node.innerCapabilities?.length) return null;

  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  const { sx: cx, sy: cy } = toScreen(node.x, node.y);
  const screenR = node.radius * transform.scale;

  // Viewport cull
  if (
    cx + screenR < 0 ||
    cx - screenR > canvasWidth ||
    cy + screenR < 0 ||
    cy - screenR > canvasHeight
  ) return null;

  const entries = node.innerCapabilities;
  const CARD_W = Math.max(130, Math.min(190, screenR * 0.88));
  const CARD_H = 88;
  const GAP = 8;
  const COLS = Math.max(1, Math.min(4, Math.ceil(Math.sqrt(entries.length))));
  const ROWS = Math.ceil(entries.length / COLS);
  const gridW = COLS * CARD_W + (COLS - 1) * GAP;
  const gridH = ROWS * CARD_H + (ROWS - 1) * GAP;

  return (
    <div
      style={{
        position: 'absolute',
        left: cx - gridW / 2,
        top: cy - gridH / 2,
        width: gridW,
        pointerEvents: 'none',
        zIndex: 20,
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, ${CARD_W}px)`,
        gap: GAP,
      }}
    >
      {entries.map((ic) => {
        const entry: CapabilityEntry = ic.data;
        const color = getDomainColor(entry.domain);
        const pct = Math.max(0, Math.min(10, entry.score)) / 10;

        // Score ring SVG
        const RING_SIZE = 32;
        const r = (RING_SIZE - 6) / 2;
        const circ = 2 * Math.PI * r;

        // Score tier
        const tier =
          entry.score >= 8 ? { label: 'High', bg: darkMode ? '#14532d' : '#dcfce7', text: '#22c55e' } :
          entry.score >= 5 ? { label: 'Mid',  bg: darkMode ? '#1e3a5f' : '#dbeafe', text: '#3b82f6' } :
                             { label: 'Low',  bg: darkMode ? '#7f1d1d' : '#fee2e2', text: '#ef4444' };

        const domainBg = darkMode
          ? (DOMAIN_BG_DARK[entry.domain as CapabilityDomain] ?? '#374151')
          : (DOMAIN_BG_LIGHT[entry.domain as CapabilityDomain] ?? '#f3f4f6');

        const reportPreview = entry.report
          ? entry.report.split('\n')[0].slice(0, 70)
          : null;

        const borderColor =
          entry.score >= 8 ? '#22c55e' :
          entry.score >= 5 ? '#3b82f6' :
                             '#ef4444';

        return (
          <div
            key={ic.id}
            style={{
              width: CARD_W,
              height: CARD_H,
              border: `1.5px solid ${borderColor}`,
              borderRadius: 8,
              background: darkMode ? 'rgba(17,24,39,0.92)' : 'rgba(255,255,255,0.96)',
              boxShadow: `0 0 6px ${borderColor}33`,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Top accent bar — score-tier color */}
            <div style={{ height: 3, flexShrink: 0, backgroundColor: borderColor }} />

            <div style={{ padding: '5px 7px', display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0 }}>

              {/* Row 1: score ring + label + tier badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                <svg width={RING_SIZE} height={RING_SIZE} style={{ flexShrink: 0 }}>
                  <circle
                    cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={r}
                    fill="none"
                    stroke={darkMode ? '#374151' : '#e5e7eb'}
                    strokeWidth={4}
                  />
                  <circle
                    cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={r}
                    fill="none"
                    stroke={color}
                    strokeWidth={4}
                    strokeDasharray={circ}
                    strokeDashoffset={circ * (1 - pct)}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
                  />
                  <text
                    x={RING_SIZE / 2} y={RING_SIZE / 2 + 3}
                    textAnchor="middle"
                    fontSize={9}
                    fontWeight="bold"
                    fill={color}
                  >
                    {entry.score}
                  </text>
                </svg>

                <span style={{
                  fontSize: 9,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  color: darkMode ? '#f3f4f6' : '#111827',
                }}>
                  {entry.label}
                </span>

                <span style={{
                  fontSize: 7,
                  fontWeight: 600,
                  flexShrink: 0,
                  padding: '1px 4px',
                  borderRadius: 99,
                  backgroundColor: tier.bg,
                  color: tier.text,
                }}>
                  {tier.label}
                </span>
              </div>

              {/* Row 2: domain badge */}
              <div style={{
                alignSelf: 'flex-start',
                padding: '1px 5px',
                borderRadius: 99,
                fontSize: 7,
                fontWeight: 600,
                backgroundColor: domainBg,
                color: color,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                maxWidth: '100%',
                textOverflow: 'ellipsis',
              }}>
                {entry.domain.replace(/_/g, ' ')}
              </div>

              {/* Row 3: report preview */}
              {reportPreview && (
                <p style={{
                  fontSize: 7.5,
                  color: darkMode ? '#9ca3af' : '#6b7280',
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  margin: 0,
                  lineHeight: 1.35,
                }}>
                  {reportPreview}{entry.report && entry.report.length > 70 ? '…' : ''}
                </p>
              )}

              {/* Row 4: floor / ceiling pills */}
              {(entry.ceiling || entry.floor) && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {entry.ceiling && (
                    <span style={{
                      fontSize: 6,
                      padding: '1px 4px',
                      borderRadius: 4,
                      backgroundColor: darkMode ? '#14532d44' : '#dcfce7',
                      color: '#22c55e',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      maxWidth: 80,
                      textOverflow: 'ellipsis',
                    }} title={`Ceiling: ${entry.ceiling}`}>
                      ↑ {entry.ceiling.slice(0, 18)}{entry.ceiling.length > 18 ? '…' : ''}
                    </span>
                  )}
                  {entry.floor && (
                    <span style={{
                      fontSize: 6,
                      padding: '1px 4px',
                      borderRadius: 4,
                      backgroundColor: darkMode ? '#7f1d1d44' : '#fee2e2',
                      color: '#ef4444',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      maxWidth: 80,
                      textOverflow: 'ellipsis',
                    }} title={`Floor: ${entry.floor}`}>
                      ↓ {entry.floor.slice(0, 18)}{entry.floor.length > 18 ? '…' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CapabilityClusterOverlay;
